import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { type IconSizes } from '@app/app/shared/styleguide/components/icon/icon.component';
import { Tick02Icon } from '@hugeicons/core-free-icons/index';
import { LucideCheck } from '@lucide/angular';

export type SgCheckboxSize = 's' | 'm' | 'l';
export type SgCheckboxType = 'default' | 'indigo' | 'green';

@Component({
  selector: 'ptl-sg-checkbox',
  templateUrl: './sg-checkbox.component.html',
  styleUrls: ['./sg-checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SgCheckboxComponent),
      multi: true,
    },
  ],
})
export class SgCheckboxComponent implements ControlValueAccessor {
  protected readonly Tick02Icon = Tick02Icon;

  @Input() checked = false;
  @Input() disabled = false;
  @Input() ariaLabel: string;
  @Input() size: SgCheckboxSize = 'm';
  @Input() type: SgCheckboxType = 'default';
  /**
   * Only asks for the change: emits the requested value but keeps showing `[checked]`, so a host that declines it has
   * nothing to put back. For `[checked]` + `(checkboxChange)`; it doesn't report to `ngModel` / `formControl`.
   */
  @Input() controlled = false;

  @Output() checkboxChange = new EventEmitter<boolean>();

  private static readonly iconSizeMap: Record<SgCheckboxSize, IconSizes> = {
    s: 'xs',
    m: 'xs',
    l: 's',
  };

  get iconSize(): IconSizes {
    return SgCheckboxComponent.iconSizeMap[this.size];
  }

  private onChange = (value: boolean) => {
    // ControlValueAccessor callback
  };
  private onTouched = () => {
    // ControlValueAccessor callback
  };

  writeValue(value: boolean): void {
    this.checked = value || false;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onToggle(): void {
    if (this.disabled) {
      return;
    }
    const next = !this.checked;
    if (!this.controlled) {
      this.checked = next;
      this.onChange(next);
    }
    this.checkboxChange.emit(next);
  }

  onBlur(): void {
    this.onTouched();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.onToggle();
    }
  }

  protected readonly LucideCheck = LucideCheck;
}
